import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, CheckCircle } from 'lucide-react';
import { articleService, authorService } from '../services/api';

function SubmitArticle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [authors, setAuthors] = useState([{ firstName: '', lastName: '', email: '', affiliation: '' }]);
  const [formData, setFormData] = useState({
    title: '',
    abstract: '',
    keywords: '',
    content: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAuthorChange = (index, field, value) => {
    const newAuthors = [...authors];
    newAuthors[index][field] = value;
    setAuthors(newAuthors);
  };

  const addAuthor = () => {
    setAuthors([...authors, { firstName: '', lastName: '', email: '', affiliation: '' }]);
  };

  const removeAuthor = (index) => {
    setAuthors(authors.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const createdAuthors = await Promise.all(
        authors.map(author => authorService.create(author))
      );

      const article = {
        ...formData,
        articleAuthors: createdAuthors.map((author, index) => ({
          authorId: author.data.id,
          order: index,
        })),
      };

      await articleService.create(article);
      setSuccess(true);
      setTimeout(() => navigate('/articles'), 2000);
    } catch (err) {
      setError('خطا در ارسال مقاله. لطفاً دوباره تلاش کنید.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">ارسال مقاله جدید</h1>
          <p className="text-xl text-blue-100">نشریه مجری منتظر دریافت مقالات علمی شما است</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {success && (
          <div className="bg-green-50 border-r-4 border-green-600 text-green-800 p-6 rounded-lg mb-8 flex items-start gap-3">
            <CheckCircle size={24} className="flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold mb-1">مقاله شما با موفقیت ارسال شد</h3>
              <p>در حال هدایت شما به صفحه مقالات...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-r-4 border-red-600 text-red-800 p-6 rounded-lg mb-8">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8">
          {/* Article Info Section */}
          <div className="mb-10 pb-10 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">اطلاعات مقاله</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-900 mb-3">عنوان مقاله *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="عنوان مقاله را وارد کنید"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-900 mb-3">چکیده *</label>
              <textarea
                name="abstract"
                value={formData.abstract}
                onChange={handleInputChange}
                required
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="چکیده مقاله را وارد کنید"
              />
              <p className="text-sm text-gray-500 mt-2">حداکثر 500 کلمه</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-900 mb-3">کلمات کلیدی *</label>
              <input
                type="text"
                name="keywords"
                value={formData.keywords}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="کلمات کلیدی را با کاما جدا کنید (مثال: معماری، ساختمان، طراحی)"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-900 mb-3">متن کامل مقاله *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                required
                rows="12"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono text-sm"
                placeholder="متن کامل مقاله را وارد کنید"
              />
            </div>
          </div>

          {/* Authors Section */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">نویسندگان</h2>
            <div className="space-y-6">
              {authors.map((author, index) => (
                <div key={index} className="bg-gradient-to-br from-blue-50 to-gray-50 p-6 rounded-lg border border-blue-100">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-gray-900">نویسنده {index + 1}</h3>
                    {authors.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAuthor(index)}
                        className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm font-medium"
                      >
                        <Trash2 size={16} />
                        حذف
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="نام"
                      value={author.firstName}
                      onChange={(e) => handleAuthorChange(index, 'firstName', e.target.value)}
                      required
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="نام خانوادگی"
                      value={author.lastName}
                      onChange={(e) => handleAuthorChange(index, 'lastName', e.target.value)}
                      required
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                    <input
                      type="email"
                      placeholder="ایمیل"
                      value={author.email}
                      onChange={(e) => handleAuthorChange(index, 'email', e.target.value)}
                      required
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder="تعلق سازمانی"
                      value={author.affiliation}
                      onChange={(e) => handleAuthorChange(index, 'affiliation', e.target.value)}
                      required
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addAuthor}
              className="mt-6 text-blue-700 hover:text-blue-900 font-bold text-sm flex items-center gap-2"
            >
              <Plus size={18} />
              افزودن نویسنده دیگر
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-700 to-blue-900 text-white py-4 rounded-lg font-bold hover:from-blue-800 hover:to-blue-950 transition disabled:from-gray-400 disabled:to-gray-500 shadow-lg hover:shadow-xl"
          >
            {loading ? 'در حال ارسال...' : 'ارسال مقاله'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SubmitArticle;
